import type { SearchFilters } from "../FilterBar/filterChipDefs";
import type { GetInteractionsRequest } from "../../../api/interaction";

// Filter keys V2 supports. Drives both `filtersEqual` (modified-state diffing)
// and the V1 ↔ V2 conversion helpers below. Kept in sync with the
// `SearchFilters` interface in `../FilterBar/filterChipDefs.ts`.
const SEARCH_FILTER_KEYS: (keyof SearchFilters)[] = [
  "from", "to",
  "agentName", "agentNameExactMatch", "agentId",
  "siteIds", "interactionType",
  "tags", "direction",
  "phoneNumber", "dnis", "extension", "device",
  "group", "skill",
  "legalHold", "hasMedia",
  "minDuration", "maxDuration",
  "fromRetention", "toRetention", "hasRetention",
  "sourceId", "interactionId", "masterInteractionId", "segmentId", "callId",
  "hasTranscription",
];

// Array-valued filter keys compared as unordered sets in `filtersEqual`.
const ARRAY_KEYS: ReadonlySet<keyof SearchFilters> = new Set(["tags", "siteIds"]);

function arraysEqualAsSet(a?: string[], b?: string[]): boolean {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa.length !== bb.length) return false;
  const s = new Set(aa);
  return bb.every((v) => s.has(v));
}

export function filtersEqual(a: SearchFilters, b: SearchFilters): boolean {
  for (const key of SEARCH_FILTER_KEYS) {
    if (ARRAY_KEYS.has(key)) {
      if (!arraysEqualAsSet(a[key] as string[] | undefined, b[key] as string[] | undefined)) {
        return false;
      }
      continue;
    }
    // Normalize null/undefined to the same thing — the backend serializes unset
    // fields as null, the UI state leaves them undefined; they mean the same thing.
    const av = a[key] ?? undefined;
    const bv = b[key] ?? undefined;
    if (av !== bv) return false;
  }
  return true;
}

export function toSearchFilters(req: GetInteractionsRequest): SearchFilters {
  const filters: SearchFilters = {};
  for (const key of SEARCH_FILTER_KEYS) {
    const value = (req as any)[key];
    // Skip null as well as undefined. The backend normalizes unset fields to
    // null in saved-search payloads (default C# nullable-property serialization).
    if (value != null) (filters as any)[key] = value;
  }
  return filters;
}

export function toGetInteractionsRequest(filters: SearchFilters): GetInteractionsRequest {
  const out: GetInteractionsRequest = {};
  for (const key of SEARCH_FILTER_KEYS) {
    const v = (filters as any)[key];
    if (v != null) (out as any)[key] = v;
  }
  return out;
}
